import { useCallback } from "react";
import { useSearchParams } from "react-router";
import type { FilterState } from "../../ProjectList/types";
import {
  FilterKey,
  getSmartCondition,
  getAvailableConditions,
} from "@/types/filterEnums";
import {
  serializeFiltersToParams,
  getDefaultFilters,
} from "@/utils/filterUrlUtils";

export type FilterValue = string | number;

export const createDefaultFilters = (): FilterState => getDefaultFilters();

export const removeFilterValue = <T extends FilterValue>(
  currentValues: T[] | undefined,
  valueToRemove: string,
): T[] => {
  if (!currentValues) return [];

  const parsedValue = isNaN(Number(valueToRemove))
    ? valueToRemove
    : Number(valueToRemove);
  return currentValues.filter((val) => val !== parsedValue);
};

export const updateDueDateFilter = (
  currentDueDates: FilterState["dueDates"],
  key: string,
  value: boolean,
): FilterState["dueDates"] => {
  if (!currentDueDates) return { [key]: value };
  return { ...currentDueDates, [key]: value };
};

export { getSmartCondition, getAvailableConditions };

export const useFilterOperations = (
  projectId: string | undefined,
  filters: FilterState | undefined,
) => {
  const [, setSearchParams] = useSearchParams();

  const removeFilter = useCallback(
    (filterType: FilterKey, value: string) => {
      if (!projectId || !filters) return;

      let updatedFilters: FilterState;

      switch (filterType) {
        case FilterKey.SEARCH:
          updatedFilters = { ...filters, search: "" };
          break;
        case FilterKey.USER_IDS:
          updatedFilters = {
            ...filters,
            userIds: removeFilterValue(filters.userIds, value),
          };
          break;
        case FilterKey.LABELS:
          updatedFilters = {
            ...filters,
            labels: removeFilterValue(filters.labels, value),
          };
          break;
        case FilterKey.STATUSES:
          updatedFilters = {
            ...filters,
            statuses: removeFilterValue(filters.statuses, value),
          };
          break;
        case FilterKey.ESTIMATES:
          updatedFilters = {
            ...filters,
            estimates: removeFilterValue(filters.estimates, value),
          };
          break;
        case FilterKey.PRIORITIES:
          updatedFilters = {
            ...filters,
            priorities: removeFilterValue(filters.priorities, value),
          };
          break;
        case FilterKey.DUE_DATES:
          updatedFilters = {
            ...filters,
            dueDates: updateDueDateFilter(filters.dueDates, value, false),
          };
          break;
        default:
          return;
      }

      setSearchParams((prev) => serializeFiltersToParams(prev, updatedFilters));
    },
    [projectId, filters, setSearchParams],
  );

  const removeEntireFilter = useCallback(
    (filterType: FilterKey) => {
      if (!projectId || !filters) return;

      let updatedFilters: FilterState;

      switch (filterType) {
        case FilterKey.SEARCH:
          updatedFilters = { ...filters, search: "" };
          break;
        case FilterKey.USER_IDS:
          updatedFilters = { ...filters, userIds: [] };
          break;
        case FilterKey.LABELS:
          updatedFilters = { ...filters, labels: [] };
          break;
        case FilterKey.STATUSES:
          updatedFilters = { ...filters, statuses: [] };
          break;
        case FilterKey.ESTIMATES:
          updatedFilters = { ...filters, estimates: [] };
          break;
        case FilterKey.PRIORITIES:
          updatedFilters = { ...filters, priorities: [] };
          break;
        case FilterKey.DUE_DATES:
          updatedFilters = {
            ...filters,
            dueDates: {
              none: false,
              overdue: false,
              today: false,
              thisWeek: false,
              nextWeek: false,
              later: false,
            },
          };
          break;
        default:
          return;
      }

      setSearchParams((prev) => serializeFiltersToParams(prev, updatedFilters));
    },
    [projectId, filters, setSearchParams],
  );

  const resetFilters = useCallback(() => {
    if (!projectId) return;
    setSearchParams((prev) =>
      serializeFiltersToParams(prev, getDefaultFilters()),
    );
  }, [projectId, setSearchParams]);

  const updateFilter = useCallback(
    (filterType: string, value: string | number, condition?: string) => {
      if (!projectId || !filters || !condition) return;

      const updatedFilters = {
        ...filters,
        conditions: {
          ...filters.conditions,
          [filterType]: condition,
        },
      };

      setSearchParams((prev) => serializeFiltersToParams(prev, updatedFilters));
    },
    [projectId, filters, setSearchParams],
  );

  const updateConditionsForValues = useCallback(
    (filterType: FilterKey, values: (string | number)[] | undefined) => {
      if (!projectId || !filters) return;

      const currentCondition = filters.conditions?.[filterType];
      const smartCondition = getSmartCondition(values, currentCondition);

      if (currentCondition !== smartCondition) {
        const updatedFilters = {
          ...filters,
          conditions: {
            ...filters.conditions,
            [filterType]: smartCondition,
          },
        };
        setSearchParams((prev) =>
          serializeFiltersToParams(prev, updatedFilters),
        );
      }
    },
    [projectId, filters, setSearchParams],
  );

  return {
    removeFilter,
    removeEntireFilter,
    resetFilters,
    updateFilter,
    updateConditionsForValues,
    createDefaultFilters,
    getSmartCondition,
    getAvailableConditions,
  };
};
